import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { Alert } from '../ui/Alert';
import Skeleton from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import StatusPill, { humanizeEnum } from '../ui/StatusPill';
import useTableSort from '../ui/useTableSort';
import ListShell from '../ui/ListShell';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatCurrency';
import { getQuoteRoute } from '../../utils/quoteRouting';

// IDI-479: sortable columns. ID uses the numeric-aware text comparator, so
// numeric ids order 2 < 10 and non-numeric ids still sort instead of
// collapsing to "empty".
const LEAD_SORT_COLUMNS = [
  { key: 'id', type: 'text', value: (l) => (l.id == null ? null : String(l.id)) },
  { key: 'customer', type: 'text', value: (l) => l.customerName || l.customer_name || l.title || null },
  { key: 'project', type: 'text', value: (l) => l.projectName || l.project_name || l.quoteTitle || l.quote_title || null },
  { key: 'amount', type: 'money', value: (l) => l.total_amount ?? l.totalAmount ?? 0 },
  { key: 'status', type: 'text', value: (l) => humanizeEnum(l.status || 'active') || null },
  { key: 'date', type: 'date', value: (l) => l.createdAt || l.created_at || null },
];

function SkeletonRow() {
  return (
    <TableRow>
      {[...Array(6)].map((_, i) => (
        <TableCell key={i}>
          <Skeleton w={`${60 + Math.random() * 40}%`} h="1em" />
        </TableCell>
      ))}
    </TableRow>
  );
}

export default function LeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/quotes?status=lead');
      const data = response.data || response || [];
      setLeads(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError('Unable to load leads.');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = useMemo(() => {
    if (!searchTerm) return leads;
    const term = searchTerm.toLowerCase();
    return leads.filter(lead => {
      const name = (lead.customerName || lead.customer_name || lead.title || '').toLowerCase();
      const project = (lead.projectName || lead.project_name || lead.quoteTitle || '').toLowerCase();
      const id = (lead.id || '').toString().toLowerCase();
      return name.includes(term) || project.includes(term) || id.includes(term);
    });
  }, [leads, searchTerm]);

  const { sortedRows: sortedLeads, getSortProps } = useTableSort(filteredLeads, LEAD_SORT_COLUMNS);

  const metrics = useMemo(() => {
    const total = leads.length;
    const active = leads.filter(l => l.status === 'active' || l.status === 'sent' || l.status === 'draft').length;
    const won = leads.filter(l => l.status === 'won' || l.status === 'converted').length;
    const lost = leads.filter(l => l.status === 'lost').length;
    return { total, active, won, lost };
  }, [leads]);

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      const date = new Date(d);
      return isNaN(date.getTime()) ? '—' : `${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()}`;
    } catch { return '—'; }
  };

  return (
    <div className="px-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Leads</h1>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card className="p-4"><p className="text-xs text-gray-500">Total Leads</p><p className="text-2xl font-bold text-gray-900">{metrics.total}</p></Card>
        <Card className="p-4"><p className="text-xs text-gray-500">Active</p><p className="text-2xl font-bold text-green-600">{metrics.active}</p></Card>
        <Card className="p-4"><p className="text-xs text-gray-500">Won</p><p className="text-2xl font-bold text-green-600">{metrics.won}</p></Card>
        <Card className="p-4"><p className="text-xs text-gray-500">Lost</p><p className="text-2xl font-bold text-red-600">{metrics.lost}</p></Card>
      </div>

      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

      <ListShell
        search={searchTerm}
        onSearchChange={setSearchTerm}
        placeholder="Search by customer name, title, or ID..."
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader {...getSortProps('id')}>ID</TableHeader>
              <TableHeader {...getSortProps('customer')}>Customer</TableHeader>
              <TableHeader {...getSortProps('project')}>Project</TableHeader>
              <TableHeader align="right" {...getSortProps('amount')}>Amount</TableHeader>
              <TableHeader {...getSortProps('status')}>Status</TableHeader>
              <TableHeader {...getSortProps('date')}>Date</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <>
                <SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow />
              </>
            ) : filteredLeads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  <EmptyState
                    mode={leads.length === 0 ? 'first-run' : 'filtered'}
                    title={leads.length === 0 ? 'No leads yet.' : 'No leads match your search.'}
                    onClearFilters={() => setSearchTerm('')}
                  />
                </TableCell>
              </TableRow>
            ) : (
              sortedLeads.map((lead) => (
                <TableRow key={lead.id} onClick={() => navigate(getQuoteRoute(lead))} className="cursor-pointer">
                  <TableCell className="font-semibold" href={getQuoteRoute(lead)}>L-{lead.id}</TableCell>
                  <TableCell>{lead.customerName || lead.customer_name || lead.title || '—'}</TableCell>
                  <TableCell>{lead.projectName || lead.project_name || lead.quoteTitle || lead.quote_title || '—'}</TableCell>
                  {/* IDI-215: total_amount (live, editable) before totalAmount
                      (stale legacy alias) — same fix as QuotesPage.js. */}
                  <TableCell align="right">{formatCurrency(lead.total_amount ?? lead.totalAmount ?? 0)}</TableCell>
                  <TableCell><StatusPill status={lead.status || 'active'} /></TableCell>
                  <TableCell>{formatDate(lead.createdAt || lead.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ListShell>
    </div>
  );
}
